import { HttpClient, type HttpResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { createHttpContextFromOptions } from '@quipex/core/primitives';
import { BuildingDocumentsApiService } from '@quipex/shared/contracts';
import { firstValueFrom } from 'rxjs';

export interface ActivityDocumentExportDownloadRequest {
    readonly buildingPublicId: string;
    readonly jobId: string;
    readonly isCurrent?: () => boolean;
}

export interface ActivityPackExportDownloadRequest {
    readonly buildingPublicId: string;
    readonly packId: string;
    readonly jobId: string;
    readonly isCurrent?: () => boolean;
}

@Injectable()
export class ActivityExportDownloader {
    private readonly selectedInjector = inject(Injector);
    private readonly http = inject(HttpClient);

    public async downloadSelectedDocuments(
        request: ActivityDocumentExportDownloadRequest
    ): Promise<void> {
        const status = await firstValueFrom(
            this.selectedInjector
                .get(BuildingDocumentsApiService)
                .getBuildingDocumentExportById(
                    request.buildingPublicId,
                    request.jobId,
                    {
                        context: createHttpContextFromOptions({
                            // Activity checks selected archive availability here and reports failures beside its download action.
                            suppressAutoErrorToast: true,
                        }),
                    }
                )
        );
        if (request.isCurrent?.() === false) return;
        if (
            status.jobId !== request.jobId ||
            status.jobType !== 'SelectedBuildingDocumentExport' ||
            status.status !== 'completed' ||
            status.downloadAvailable !== true ||
            (status.expiresAt != null &&
                (typeof status.expiresAt !== 'string' ||
                    !Number.isFinite(Date.parse(status.expiresAt)) ||
                    Date.parse(status.expiresAt) <= Date.now()))
        )
            throw new TypeError('Selected archive is unavailable.');
        await this.downloadDocuments({
            ...request,
            isCurrent: () =>
                request.isCurrent?.() !== false &&
                (!status.expiresAt ||
                    Date.parse(status.expiresAt) > Date.now()),
        });
    }

    public async downloadDocuments(
        request: ActivityDocumentExportDownloadRequest
    ): Promise<void> {
        const response = await firstValueFrom(
            this.getArchive(
                `/@core/api/buildings/${request.buildingPublicId}/document-exports/${request.jobId}/download`
            )
        );
        this.saveResponse(
            response,
            request.isCurrent,
            'Quipex-building-documents.zip'
        );
    }

    public async downloadPack(
        request: ActivityPackExportDownloadRequest
    ): Promise<void> {
        const response = await firstValueFrom(
            this.getArchive(
                `/@core/api/buildings/${request.buildingPublicId}/document-packs/${request.packId}/exports/${request.jobId}/download`
            )
        );
        this.saveResponse(
            response,
            request.isCurrent,
            'Quipex-document-pack.zip'
        );
    }

    private getArchive(url: string) {
        return this.http.get(url, {
            context: createHttpContextFromOptions({
                // Activity owns download recovery and reports failures beside the action that initiated the ZIP download.
                suppressAutoErrorToast: true,
            }),
            observe: 'response',
            responseType: 'blob',
        });
    }

    private saveResponse(
        response: HttpResponse<Blob>,
        isCurrent: (() => boolean) | undefined,
        fallbackFileName: string
    ): void {
        if (isCurrent?.() === false) return;
        if (!response.body || response.body.size === 0) {
            throw new TypeError('The archive response did not contain a file.');
        }

        saveArchive(
            response.body,
            archiveFileName(
                response.headers.get('Content-Disposition'),
                fallbackFileName
            )
        );
    }
}

function archiveFileName(
    contentDisposition: string | null,
    fallback: string
): string {
    const encodedName = contentDisposition?.match(
        /filename\*=UTF-8'(?:[^']*)'([^;]+)/i
    )?.[1];
    const quotedName = contentDisposition?.match(/filename="([^"]+)"/i)?.[1];
    const tokenName = contentDisposition?.match(/filename=([^;\s"]+)/i)?.[1];
    const candidate = encodedName
        ? decodeFileName(encodedName)
        : (quotedName ?? tokenName);

    return candidate?.toLowerCase().endsWith('.zip') &&
        !/[\\/\r\n]/.test(candidate)
        ? candidate
        : fallback;
}

function decodeFileName(value: string): string | null {
    try {
        return decodeURIComponent(value);
    } catch {
        return null;
    }
}

function saveArchive(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url));
}
